/**
 * What a role is producing, as it produces it (ADR-114).
 *
 * Explicitly not the record: bounded, truncatable, and with numbered slices so
 * a missed one is *said* rather than smoothed over (§16.4). The authoritative
 * output is the `assistant_message` written to the logical thread when the
 * attempt settles.
 */

import { useEffect, useRef, type ReactNode } from 'react';

import type { LiveTranscript } from '../../store.js';

export function LiveOutput({
  transcript,
  working,
}: {
  transcript: LiveTranscript | null;
  working: boolean;
}): ReactNode {
  const paneRef = useRef<HTMLPreElement>(null);

  // Text arriving under the reader must not leave them looking at old output.
  useEffect(() => {
    const pane = paneRef.current;
    if (pane !== null) pane.scrollTop = pane.scrollHeight;
  }, [transcript?.text]);

  if (transcript === null) {
    return <p className="agent-live-empty">Waiting for the first output…</p>;
  }

  const tail = transcript.newestSlice;
  const settled = transcript.text.endsWith(tail)
    ? transcript.text.slice(0, transcript.text.length - tail.length)
    : transcript.text;

  return (
    <div className="agent-live-output">
      {transcript.gap ? (
        <p className="agent-live-gap">A slice of this output was missed. What follows is real, but it does not join up.</p>
      ) : null}
      <pre className={working ? 'agent-live-text agent-live-caret' : 'agent-live-text'} tabIndex={0} ref={paneRef}>
        {settled}
        <span className="agent-live-slice">{transcript.text.endsWith(tail) ? tail : ''}</span>
      </pre>
    </div>
  );
}
